import { useMemo } from "react";
import { useOpsStore } from "../state/opsStore";
import "./BottleneckHeatmap.css";

/**
 * Phase 18 WU-18.D7 — Bottleneck Heatmap.
 *
 * WU-18.E5 refactor: presentational core is now exported as
 * `BottleneckHeatmapView` so the Analytics SLA tab can reuse the same
 * layout. This default export stays as the Ops-Console-wired variant.
 *
 * Each row renders: step · filled bar (width ∝ stallCount / max) · meta
 * "{stallCount} docs, avg {Xs} ⚠" when avg > 1.5x historical.
 *
 * Empty state when no bottleneck has fired.
 */

export interface BottleneckRowView {
  step: string;
  /** Magnitude used for the filled-bar width. */
  magnitude: number;
  /** Optional magnitude label (e.g. "3 docs"). */
  magnitudeLabel?: string;
  /** Display value for the current duration. Renders as "{label}". */
  avgLabel: string;
  /** True to render the ⚠ warn flag. */
  warn: boolean;
}

export interface BottleneckHeatmapViewProps {
  rows: BottleneckRowView[];
  emptyMessage?: string;
  testId?: string;
}

function fmtSeconds(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return "—";
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}m ${s}s`;
}

export function BottleneckHeatmapView({
  rows,
  emptyMessage = "No bottlenecks detected.",
  testId = "ops-bottleneck-heatmap",
}: BottleneckHeatmapViewProps) {
  if (rows.length === 0) {
    return (
      <div
        className="ops-bottleneck-heatmap__empty"
        data-testid={`${testId}-empty`}
      >
        {emptyMessage}
      </div>
    );
  }

  const maxMagnitude = Math.max(...rows.map((r) => r.magnitude), 1);

  return (
    <div
      className="ops-bottleneck-heatmap"
      data-testid={testId}
    >
      {rows.map((r) => {
        const widthPct = Math.max(4, Math.round((r.magnitude / maxMagnitude) * 100));
        const fillClass = `ops-bottleneck-row__fill${r.warn ? " ops-bottleneck-row__fill--warn" : ""}`;
        return (
          <div key={r.step} className="ops-bottleneck-row" data-step={r.step}>
            <span className="ops-bottleneck-row__step">{r.step}</span>
            <div className="ops-bottleneck-row__bar">
              <div className={fillClass} style={{ width: `${widthPct}%` }} />
            </div>
            <span className="ops-bottleneck-row__meta">
              {r.magnitudeLabel ?? r.magnitude}
              {r.magnitudeLabel ? "" : ""}, avg {r.avgLabel}
              {r.warn && (
                <span className="ops-bottleneck-row__warn" aria-label="above historical">
                  {" "}⚠
                </span>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Ops Console wrapper — sources rows from opsStore.bottlenecks.
 */
export function BottleneckHeatmap() {
  const bottlenecks = useOpsStore((s) => s.bottlenecks);
  const rows = useMemo<BottleneckRowView[]>(() => {
    const arr = bottlenecks.slice();
    arr.sort((a, b) => b.stallCount - a.stallCount);
    return arr.map((b) => ({
      step: b.step,
      magnitude: b.stallCount,
      magnitudeLabel: `${b.stallCount} docs`,
      avgLabel: fmtSeconds(b.avgMs),
      warn: b.avgMs > b.historicalAvgMs * 1.5,
    }));
  }, [bottlenecks]);

  return (
    <BottleneckHeatmapView
      rows={rows}
      emptyMessage="No bottlenecks detected in the last 15 minutes."
    />
  );
}
